import { isNil } from 'ramda';

import {
  requestOffersHistoryListingActions,
  saveDefaultFilters,
  setRoomsFilter,
  setStatusFilter,
  setTypeFilter,
} from './actions';
import {
  fetchOffersHistoryListingByHouseId,
  fetchOffersHistoryListingByOfferId,
} from '../../services/offersHistory/fetchOffersHistoryListing';
import {
  ETypeFilterValues,
  FILTER_ALL_VALUE,
  mapOfferLocationsEnums,
  mapOfferStatusEnums,
  mapOfferRoomsEnums,
  IOffersHistoryListingData,
} from '../../types/offerHistory';
import { TThunkAction } from '../../types/redux';
import { fetchOffersHistorySuburbanListing } from '../../services/offersHistory/fetchOffersHistorySuburbanListing';
import { selectIsSuburban } from '../../selectors/filters';
import { selectDealType } from '../../selectors/page';

const { success, successLoadMore, request, failure } = requestOffersHistoryListingActions;

const getSuccessAction = (isLoadMore?: boolean) => (isLoadMore ? successLoadMore : success);

/**
 * Экшен запросов за листингом истории объявлений:
 *  - если отчет открыт в режиме оценки загородки (дома/участки)
 *      делаем запрос за историей загородки
 *  - если отчет открыт в режиме оценки конкретного объявления (вторичка квартира)
 *      делаем запрос за историей объявлений с offerId
 *  - во всех остальных случаях запрос происходит через houseId
 */
export const getOffersHistory =
  (isLoadMore?: boolean): TThunkAction<Promise<void>> =>
  async (dispatch, getState, context) => {
    const state = getState();
    const {
      offersHistory: {
        page,
        roomsFilterActiveValue,
        statusFilterActiveValue,
        categoriesFilterActiveValue,
        typeFilterActiveValue,
        resultsOnPage,
      },
      filters: { houseId, area },
      page: {
        isFromOfferCard,
        queryParams: { offerId },
        minGeoId,
      },
    } = state;

    const isSuburban = selectIsSuburban(state);
    const dealType = selectDealType(state);

    dispatch(request(!!isLoadMore));

    let res: IOffersHistoryListingData;

    const basedHistoryParams = {
      resultsOnPage,
      page,
    };

    try {
      if (isSuburban) {
        res = await fetchOffersHistorySuburbanListing(context, {
          ...basedHistoryParams,
          minGeoId: minGeoId as number,
          category: filterOutDefaultVal(categoriesFilterActiveValue),
          offerStatus: filterOutDefaultVal(statusFilterActiveValue),
        });
      } else {
        const flatHistoryParams = {
          ...basedHistoryParams,
          dealType,
          offerStatus: filterOutDefaultVal(statusFilterActiveValue),
          roomsCount: filterOutDefaultVal(roomsFilterActiveValue),
          isNearby: isNil(typeFilterActiveValue) ? undefined : typeFilterActiveValue === ETypeFilterValues.NearbyHouses,
        };

        res =
          isFromOfferCard && offerId
            ? await fetchOffersHistoryListingByOfferId(context, {
                ...flatHistoryParams,
                offerId,
                category: filterOutDefaultVal(categoriesFilterActiveValue),
              })
            : await fetchOffersHistoryListingByHouseId(context, {
                ...flatHistoryParams,
                houseId: houseId as number,
                totalArea: area as number,
              });
      }

      /** Предустановка активных значений из ответа API, если таковые есть, или дефолтов */
      if (res.activeFiltersValues) {
        const { locations: _locations, statusCounts: _statusCounts, roomCounts: _roomCounts } = res.activeFiltersValues;

        const location = mapOfferLocationsEnums[_locations];
        const statusCounts = _statusCounts ? mapOfferStatusEnums[_statusCounts] : FILTER_ALL_VALUE;
        const roomCounts = _roomCounts ? mapOfferRoomsEnums[_roomCounts] : FILTER_ALL_VALUE;

        dispatch(setTypeFilter(location));
        dispatch(setStatusFilter(statusCounts));
        dispatch(setRoomsFilter(roomCounts));
        dispatch(saveDefaultFilters({ location, roomCounts, statusCounts }));
      }

      dispatch(getSuccessAction(isLoadMore)(res));
    } catch (e) {
      dispatch(failure());
    }
  };

/** Отфильтровывает значение 'all' из фильтра, т.к в api оно не должно отправляться */
function filterOutDefaultVal<V extends string | undefined>(value: V | typeof FILTER_ALL_VALUE): V | undefined {
  return value === FILTER_ALL_VALUE ? undefined : value;
}
